/**
 * GymProfileManager
 *
 * Inline section for SettingsPage that lets users:
 * - See all gym profiles with their equipment
 * - Switch the active profile (updates settings + gymProfiles table)
 * - Add a new profile (name + equipment)
 * - Delete non-active profiles
 * - Rename any profile
 */

import { useState, useCallback } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';
import type { GymProfile, Equipment } from '@/types';

const EQUIPMENT_OPTIONS: { id: Equipment; label: string; icon: string }[] = [
  { id: 'barbell',    label: 'Barbell',         icon: '🏋️' },
  { id: 'dumbbell',   label: 'Dumbbells',        icon: '💪' },
  { id: 'cable',      label: 'Cable Machine',    icon: '🔗' },
  { id: 'machine',    label: 'Machines',         icon: '⚙️' },
  { id: 'kettlebell', label: 'Kettlebells',      icon: '🔔' },
  { id: 'ez_bar',     label: 'EZ Bar',           icon: '〰️' },
  { id: 'trap_bar',   label: 'Trap Bar',         icon: '⬡' },
  { id: 'bodyweight', label: 'Bodyweight',       icon: '🤸' },
  { id: 'bands',      label: 'Bands',            icon: '🎗️' },
];

// ── Add Profile Sheet ─────────────────────────────────────────

function AddProfileSheet({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const [equipment, setEquipment] = useState<Equipment[]>(['barbell', 'dumbbell', 'cable', 'machine', 'bodyweight']);
  const [saving, setSaving] = useState(false);

  const toggle = (eq: Equipment) =>
    setEquipment((prev) =>
      prev.includes(eq) ? prev.filter((e) => e !== eq) : [...prev, eq],
    );

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    const id = 'profile_' + Date.now();
    await db.gymProfiles.put({
      id,
      name: trimmed,
      availableEquipment: equipment,
      isActive: false,
      createdAt: new Date().toISOString(),
    });
    setSaving(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-surface animate-slide-up">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 pt-12 pb-4 border-b border-surface-400">
        <button className="btn-ghost px-2 py-2 text-accent" onClick={onClose}>← Back</button>
        <h2 className="text-lg font-bold text-ink flex-1">New Gym Profile</h2>
        <button
          className="btn-primary px-4 py-2 text-sm"
          onClick={handleSave}
          disabled={!name.trim() || saving}
        >
          {saving ? '…' : 'Save'}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
        {/* Name */}
        <div>
          <p className="section-header">Profile Name</p>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Home Gym, Planet Fitness"
            className="w-full bg-surface-300 rounded-xl px-4 py-3 text-ink text-sm
                       focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>

        {/* Equipment */}
        <div>
          <p className="section-header">Available Equipment</p>
          <div className="card space-y-0">
            {EQUIPMENT_OPTIONS.map((eq) => (
              <div key={eq.id}
                className="flex items-center justify-between py-3 border-b border-surface-400 last:border-0">
                <span className="text-sm text-ink">{eq.icon}  {eq.label}</span>
                <button
                  role="switch"
                  aria-checked={equipment.includes(eq.id)}
                  onClick={() => toggle(eq.id)}
                  className={`w-12 h-7 rounded-full flex items-center px-0.5 transition-colors duration-200
                              ${equipment.includes(eq.id) ? 'bg-accent' : 'bg-surface-400'}`}
                >
                  <div className={`w-6 h-6 rounded-full bg-white shadow transition-transform duration-200
                                   ${equipment.includes(eq.id) ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Profile Row ───────────────────────────────────────────────

function ProfileRow({ profile, onSwitch }: { profile: GymProfile; onSwitch: (id: string) => void }) {
  const [renaming, setRenaming] = useState(false);
  const [nameInput, setNameInput] = useState(profile.name);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleRename = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed) { setRenaming(false); return; }
    await db.gymProfiles.update(profile.id, { name: trimmed });
    setRenaming(false);
  };

  const handleDelete = async () => {
    if (profile.isActive) return; // shouldn't be reachable
    await db.gymProfiles.delete(profile.id);
    setConfirmDelete(false);
  };

  const equipmentSummary = profile.availableEquipment
    .map((e) => EQUIPMENT_OPTIONS.find((o) => o.id === e)?.icon ?? '')
    .join(' ');

  return (
    <div className={`card space-y-2 ${profile.isActive ? 'border border-accent/40' : ''}`}>
      {/* Name row */}
      <div className="flex items-center gap-2">
        {profile.isActive && (
          <span className="badge badge-accent text-xs">Active</span>
        )}
        {renaming ? (
          <input
            autoFocus
            type="text"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            onBlur={handleRename}
            onKeyDown={(e) => e.key === 'Enter' && handleRename()}
            className="flex-1 bg-surface-300 rounded-lg px-2 py-1 text-sm text-ink
                       focus:outline-none focus:ring-2 focus:ring-accent"
          />
        ) : (
          <p className="flex-1 font-semibold text-ink text-sm">{profile.name}</p>
        )}
        {!renaming && (
          <button
            className="text-xs text-ink-faint px-2 py-1 rounded-lg bg-surface-300"
            onClick={() => { setNameInput(profile.name); setRenaming(true); }}
          >
            Rename
          </button>
        )}
      </div>

      {/* Equipment summary */}
      <p className="text-xs text-ink-muted">{equipmentSummary || 'No equipment set'}</p>

      {/* Actions */}
      <div className="flex gap-2">
        {!profile.isActive && (
          <button
            className="btn-primary flex-1 text-xs py-2"
            onClick={() => onSwitch(profile.id)}
          >
            Switch to this gym
          </button>
        )}
        {!profile.isActive && !confirmDelete && (
          <button
            className="btn-danger text-xs px-3 py-2"
            onClick={() => setConfirmDelete(true)}
          >
            Delete
          </button>
        )}
        {confirmDelete && (
          <div className="flex gap-2 flex-1">
            <button className="btn-secondary flex-1 text-xs py-2" onClick={() => setConfirmDelete(false)}>Cancel</button>
            <button className="btn-danger flex-1 text-xs py-2" onClick={handleDelete}>Confirm Delete</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────

export default function GymProfileManager() {
  const [showAdd, setShowAdd] = useState(false);

  const profiles = useLiveQuery(() => db.gymProfiles.orderBy('createdAt').toArray(), []) ?? [];

  const handleSwitch = useCallback(async (id: string) => {
    // Deactivate all, activate selected
    await db.gymProfiles.toCollection().modify({ isActive: false });
    await db.gymProfiles.update(id, { isActive: true });
    await db.settings.update('singleton', { activeGymProfileId: id });
  }, []);

  return (
    <>
      <section>
        <div className="flex items-center justify-between mb-2 px-1">
          <p className="section-header mb-0">Gym Profiles</p>
          <button
            className="text-xs text-accent font-medium px-3 py-1.5 rounded-lg bg-accent/10 active:scale-95 transition-transform"
            onClick={() => setShowAdd(true)}
          >
            + Add Gym
          </button>
        </div>
        <div className="space-y-3">
          {profiles.map((p) => (
            <ProfileRow key={p.id} profile={p} onSwitch={handleSwitch} />
          ))}
        </div>
      </section>

      {showAdd && <AddProfileSheet onClose={() => setShowAdd(false)} />}
    </>
  );
}
